import * as THREE from 'three';

/**
 * Geometry from the Blender wheel packs (scripts/build-rims.mjs: public/models/rims-pack.glb, wheels-pack.glb) in the
 * form meshes.ts's Kit builds everything in: non-indexed, float position / normal / uv / color (rgb) / surf
 * (slot, roughness, metalness, channel). rimModels.ts turns the packed rims and wheels into unit wheels with it; the
 * car-kit parts in the wheels pack (the police lamp) are kept here for meshes.ts to drop into a body as it is built
 * (`kitPart`), with the procedural part as the fallback while the pack is not in. This module imports nothing of
 * meshes.ts, so meshes.ts can import it.
 */
export function toKitGeometry(src: THREE.BufferGeometry): THREE.BufferGeometry {
  const flat = src.index ? src.toNonIndexed() : src;
  const n = flat.getAttribute('position').count;
  const out = new THREE.BufferGeometry();
  // Everything as float: meshopt (scripts/compress-models.mjs) hands positions and normals over quantized, and
  // mergeGeometries wants every part's arrays of one type.
  const floats = (a: THREE.BufferAttribute, size: number): THREE.BufferAttribute => {
    const arr = new Float32Array(n * size);
    for (let i = 0; i < n; i++) {
      arr[i * size] = a.getX(i);
      arr[i * size + 1] = a.getY(i);
      if (size > 2) arr[i * size + 2] = a.getZ(i);
    }
    return new THREE.BufferAttribute(arr, size);
  };
  out.setAttribute('position', floats(flat.getAttribute('position') as THREE.BufferAttribute, 3));
  const normal = flat.getAttribute('normal') as THREE.BufferAttribute | undefined;
  if (normal) out.setAttribute('normal', floats(normal, 3));
  else out.computeVertexNormals();
  out.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(n * 2), 2));
  // colour: rgb of whatever came (u8 normalized rgba from the pack); surf: `_surf` (GLTFLoader lower-cases it);
  // the channel travels as a u8 (lamps.ts CH, an integer the shader rounds to)
  const src2 = flat.getAttribute('color') as THREE.BufferAttribute | undefined;
  const col = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    col[i * 3] = src2 ? src2.getX(i) : 0.5;
    col[i * 3 + 1] = src2 ? src2.getY(i) : 0.5;
    col[i * 3 + 2] = src2 ? src2.getZ(i) : 0.5;
  }
  out.setAttribute('color', new THREE.BufferAttribute(col, 3));
  const ss = (flat.getAttribute('_surf') ?? flat.getAttribute('surf')) as THREE.BufferAttribute | undefined;
  const surf = new Float32Array(n * 4);
  for (let i = 0; i < n; i++) {
    surf[i * 4] = ss ? Math.round(ss.getX(i)) : 1;
    surf[i * 4 + 1] = ss ? ss.getY(i) : 0.4;
    surf[i * 4 + 2] = ss ? ss.getZ(i) : 0.6;
    surf[i * 4 + 3] = ss ? Math.round(ss.getW(i) * 255) : 0;
  }
  out.setAttribute('surf', new THREE.BufferAttribute(surf, 4));
  if (flat !== src) flat.dispose();
  return out;
}

const kit = new Map<string, THREE.BufferGeometry>();
const listeners: (() => void)[] = [];

/** rimModels.ts: a car-kit part from the wheels pack, already in Kit form. */
export function registerKitPart(id: string, lod: string, geometry: THREE.BufferGeometry): void {
  kit.set(`${id}:${lod}`, geometry);
}

/** meshes.ts: a copy of a kit part (the Kit merges and disposes what it is given), or null until the pack is in. */
export function kitPart(id: string, lod: 'high' | 'mid'): THREE.BufferGeometry | null {
  const g = kit.get(`${id}:${lod}`);
  return g ? g.clone() : null;
}

/** Hear that the kit parts have arrived (meshes.ts forgets the bodies it built without them). */
export function onKitParts(cb: () => void): void {
  listeners.push(cb);
}

export function kitPartsArrived(): void {
  for (const cb of listeners) cb();
}

/** Tests: forget every kit part. */
export function resetKitParts(): void {
  kit.clear();
}
